import { Modal } from 'antd';
import useThemes from 'hooks/useThemes';
import CloseIcon from 'assets/icons/CloseIcon';
import { useAppDispatch, useAppTranslation } from 'app/hooks';
import CommentParent from './CommentParent';
import oddsApi from 'api/oddsApi';
import { useCallback, useEffect, useState } from 'react';
import { GameWithComment } from 'models/comment';
import useUserDetail from 'hooks/useUserDetail';
import { updateResult } from 'redux/gameSlice';
import { ScoreModel } from 'models';
import { MatchSummaries } from 'models/genius';
import { sportDontHaveImage } from 'api/mockSportCategory';

interface CommentModalProps {
  isShow: boolean;
  onClose?: () => void;
  gameId: string;
  title: string;
  homeScore?: string | number;
  awayScore?: string | number;
  category: string;
}

const CommentModal: React.FunctionComponent<CommentModalProps> = ({
  isShow,
  onClose,
  gameId,
  title,
  homeScore,
  awayScore,
  category,
}) => {
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const [game, setGame] = useState<GameWithComment | null>(null);
  const [comment, setComment] = useState<string>('');
  const { isUserLoggedIn } = useUserDetail();
  const dispatch = useAppDispatch();

  const getComments = useCallback(async () => {
    const { ok, body } = await oddsApi.getComment(gameId);
    if (ok && body) {
      setGame(body);
    }
  }, [gameId]);

  const handleEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendComment();
    }
  };

  useEffect(() => {
    if (isShow) getComments();
    return () => {
      if (!isShow) {
        setComment('');
        setGame(null);
      }
    };
  }, [isShow, getComments]);

  const onChangeComment = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.target.value.length < 500) {
      setComment(event.target.value);
    }
  };

  const sendComment = useCallback(async () => {
    if (comment && isUserLoggedIn) {
      const { ok } = await oddsApi.commentGame({
        content: comment,
        gameId: gameId,
      });
      if (ok) {
        setComment('');
        getComments();
        dispatch(updateResult());
      }
    }
  }, [gameId, comment]);

  return (
    <Modal
      className="common-modal comment-modal"
      title={title}
      visible={isShow}
      onCancel={onClose}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={500}
      zIndex={1100}
    >
      {game && (
        <div className="comments-container">
          <div className="scoreboard">
            <div className="team">
              {!sportDontHaveImage.includes(category) && (
                <div className="team-logo-container">
                  <img
                    src={game.homeTeamLogo}
                    alt="TERABET"
                    className="team-logo"
                    onError={(e) => {
                      e.currentTarget.src = '/no_image.png';
                    }}
                  />
                </div>
              )}

              <div className="team-name">{game.homeTeam}</div>
              <div className={`${game.status === 'LIVE' ? 'team-score-live' : 'team-score'}`}>
                {homeScore !== '' ? homeScore : '--'}
              </div>
            </div>
            <div className="vs">VS</div>
            <div className="team">
              {!sportDontHaveImage.includes(category) && (
                <div className="team-logo-container">
                  <img
                    src={game.awayTeamLogo}
                    alt="TERABET"
                    className="team-logo"
                    onError={(e) => {
                      e.currentTarget.src = '/no_image.png';
                    }}
                  />
                </div>
              )}

              <div className="team-name">{game.awayTeam}</div>
              <div className={`${game.status === 'LIVE' ? 'team-score-live' : 'team-score'}`}>
                {' '}
                {awayScore !== '' ? awayScore : '--'}
              </div>
            </div>
          </div>

          <div className="comment-list">
            {game.comments.length > 0 &&
              game.comments
                .sort((a, b) => Date.parse(b.createdDate) - Date.parse(a.createdDate))
                .map((comment) => (
                  <CommentParent
                    key={comment.id}
                    comment={comment}
                    gameId={gameId}
                    getComments={getComments}
                  />
                ))}
          </div>
          {isUserLoggedIn && (
            <div className="comment-box">
              <textarea
                placeholder="Comment..."
                onKeyUp={handleEnter}
                onChange={onChangeComment}
                value={comment}
              ></textarea>
              <div className="send-button">
                <button type="button" onClick={sendComment}>
                  {t('comment.send')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
export default CommentModal;
